import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  ClientReminderType,
  NotificationStatus,
  type ClientNotifications,
  type NotificationType,
  type OwnerNotifications,
  type PopOutMessagePayload,
  type TrainerNotifications,
} from "@fitness-app/data-models/entities/Notification";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { getPageRange } from "../../../helpers/getPageRange";
import { type AsyncThunkCreator } from "../../../index";
import { setNumberOfUnreadNotifications } from "../reducer";
import { NOTIFICATIONS_REDUCER_NAME, type NotificationsReducer } from "../types";
import { countUnreadNotifications } from "./countUnreadNotifications";

type Payload = {
  page?: number;
  filter: NotificationsReducer["filter"];
  type?: NotificationType[];
};

const isPopOutMessageExpired = (data: PopOutMessagePayload | null | undefined): boolean => {
  if (!data?.deadlineAt) {
    return false;
  }

  return data.deadlineAt <= Math.floor(Date.now() / 1000);
};

export const fetchNotifications = createAsyncThunk<
  {
    data: (ClientNotifications | OwnerNotifications | TrainerNotifications)[];
    currentPage: number;
    hasMore: boolean;
  },
  Payload,
  AsyncThunkCreator<string>
>(
  `${NOTIFICATIONS_REDUCER_NAME}/fetchNotifications`,
  async (payload, { rejectWithValue, getState, dispatch, extra: { db, auth } }) => {
    const currentUser = await getLoggedUser(auth);

    const { pageSize } = getState().notifications;

    const { start, stop, currentPage } = getPageRange(payload?.page, pageSize);

    if (currentPage === 1) {
      const { data: popOutNotifications, error: popOutError } = await db
        .from("notification")
        .select("id, data")
        .eq("ownerId", currentUser.id)
        .eq("type", ClientReminderType.PopOutMessage)
        .eq("status", NotificationStatus.UNREAD)
        .limit(120)
        .returns<{ id: string; data: PopOutMessagePayload | null }[]>();

      if (popOutError) {
        return rejectWithValue(popOutError.message);
      }

      const expiredPopOutIds = popOutNotifications
        ?.filter((notification) => isPopOutMessageExpired(notification.data))
        .map((notification) => notification.id);

      if (expiredPopOutIds?.length) {
        const { error: updateError } = await db
          .from("notification")
          .update({ status: NotificationStatus.READ, updatedAt: new Date().toISOString() })
          .in("id", expiredPopOutIds);

        if (updateError) {
          return rejectWithValue(updateError.message);
        }
      }

      // TRE-1952: the badge has to be counted over the same types the list below is filtered to.
      // Counting every type instead is what left the owner staring at a badge above an empty list —
      // the one unread notification simply was not of a type the active filter lets through.
      const count = await countUnreadNotifications(db, currentUser.id, payload.type);

      if (count !== null) {
        dispatch(setNumberOfUnreadNotifications(count));
      }
    }

    let query = db
      .from("notification")
      .select("*")
      .order("createdAt", { ascending: false })
      .eq("ownerId", currentUser.id);

    if (payload.filter === "unread") {
      query = query.eq("status", NotificationStatus.UNREAD);
    }

    if (payload.type?.length) {
      query = query.in("type", payload.type);
    }

    // One row past the page is everything "load more" needs to know. An exact count made PostgREST
    // walk every notification the filter matches before returning the first thirty — 24k rows and
    // three seconds for an owner, spent deciding whether to render a single button.
    const { error, data } = await query
      .range(start, stop + 1)
      .returns<(ClientNotifications | OwnerNotifications | TrainerNotifications)[]>();

    if (error) {
      return rejectWithValue(error.message);
    }

    return { data: data.slice(0, pageSize), currentPage, hasMore: data.length > pageSize };
  },
);
